export const SSO_LIST_PATH = "/settings/portal/sso"

// The root portal has no PortalInstance row, so it needs a stable URL segment.
export const ROOT_SSO_ID = "root"

interface SsoRow {
	isEnabled: boolean
	tokenEndpoint: string
}

export interface SsoTargetInput {
	id: string
	name: string
	slug?: string
	logoUrl?: string | null
	ssoSettings?: SsoRow | null
}

export interface SsoTarget {
	id: string
	name: string
	href: string
	url: string
	logoUrl: string | null
	/** Inherited targets fall back to the root portal's connection. */
	status: "enabled" | "paused" | "inherited"
	tokenEndpoint: string | null
}

/**
 * Root first, then every named portal. A portal with no connection of its own
 * inherits the root one, which is what the list has to say out loud.
 */
export function buildSsoTargets(
	workspace:
		| { logoUrl?: string | null; ssoSettings?: SsoRow | null }
		| null
		| undefined,
	workspaceUrl: string,
	instances: SsoTargetInput[],
): SsoTarget[] {
	const statusOf = (sso: SsoRow | null | undefined): SsoTarget["status"] =>
		sso ? (sso.isEnabled ? "enabled" : "paused") : "inherited"

	return [
		{
			id: ROOT_SSO_ID,
			name: "Root portal",
			href: `${SSO_LIST_PATH}/${ROOT_SSO_ID}`,
			url: workspaceUrl,
			logoUrl: workspace?.logoUrl ?? null,
			status: statusOf(workspace?.ssoSettings),
			tokenEndpoint: workspace?.ssoSettings?.tokenEndpoint ?? null,
		},
		...instances.map((instance) => ({
			id: instance.id,
			name: instance.name,
			href: `${SSO_LIST_PATH}/${instance.id}`,
			url: instance.slug ? `/${instance.slug}` : "",
			logoUrl: instance.logoUrl ?? null,
			status: statusOf(instance.ssoSettings),
			tokenEndpoint: instance.ssoSettings?.tokenEndpoint ?? null,
		})),
	]
}

export function ssoStateLabel(target: SsoTarget): string {
	if (target.status === "enabled") return "enabled"
	if (target.status === "paused") return "paused"
	return target.id === ROOT_SSO_ID ? "not set up" : "uses root portal"
}

/** Host of the token endpoint, so a row shows which provider it points at. */
export function endpointHost(tokenEndpoint: string | null): string | null {
	if (!tokenEndpoint) return null
	try {
		return new URL(tokenEndpoint).host
	} catch {
		return null
	}
}
